<template>
  <div class="global-container">
    <div class="content">
      <!-- Header -->
      <div class="header">
        <button class="back-button" @click="goBack">
          <span class="back-arrow">&larr;</span>
        </button>
        <div class="header-content">
          <h1 class="page-title">{{ isEditMode ? 'Edit Employee' : 'Add Employee' }}</h1>
          <p class="page-subtitle">{{ isEditMode ? 'Update employee record' : 'Create a new employee record' }}</p>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loadingEmployee" class="loading-state">
        <div class="spinner"></div>
        <p>Loading employee data...</p>
      </div>

      <!-- Form -->
      <form v-else class="employee-form" @submit.prevent="handleSubmit">
        <!-- Personal Information Section -->
        <div class="form-section">
          <div class="section-header">
            <span class="section-icon">&#x1F464;</span>
            <h2 class="section-title">Personal Information</h2>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Full Name *</label>
              <input
                v-model="formData.fullName"
                type="text"
                class="form-input"
                placeholder="Enter full name"
                required
                :disabled="loading"
              />
            </div>
            <div class="form-group">
              <label class="form-label">CNIC Number</label>
              <input
                v-model="formData.cnicNumber"
                type="text"
                class="form-input"
                placeholder="e.g. 12345-1234567-1"
                :disabled="loading"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Gender</label>
              <select v-model="formData.gender" class="form-input" :disabled="loading">
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Date of Birth</label>
              <input
                v-model="formData.dateOfBirth"
                type="date"
                class="form-input"
                :disabled="loading"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Marital Status</label>
              <select v-model="formData.maritalStatus" class="form-input" :disabled="loading">
                <option value="">Select status</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Personal Email</label>
              <input
                v-model="formData.personalEmail"
                type="email"
                class="form-input"
                placeholder="personal.email@example.com"
                :disabled="loading"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Phone</label>
              <input
                v-model="formData.phone"
                type="tel"
                class="form-input"
                placeholder="Enter phone number"
                :disabled="loading"
              />
            </div>
          </div>

          <div class="form-group full-width">
            <label class="form-label">Current Address</label>
            <textarea
              v-model="formData.currentAddress"
              class="form-textarea"
              placeholder="Enter full address"
              rows="3"
              :disabled="loading"
            ></textarea>
          </div>
        </div>

        <!-- Employment Details Section -->
        <div class="form-section">
          <div class="section-header">
            <span class="section-icon">&#x1F4BC;</span>
            <h2 class="section-title">Employment Details</h2>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Employment Type *</label>
              <select v-model="formData.employmentType" class="form-input" required :disabled="loading">
                <option value="">Select employment type</option>
                <option value="FullTime">Full Time</option>
                <option value="PartTime">Part Time</option>
                <option value="Contract">Contract</option>
                <option value="Temporary">Temporary</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Business Email</label>
              <input
                v-model="formData.businessEmail"
                type="email"
                class="form-input"
                placeholder="employee@company.com"
                :disabled="loading"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Department *</label>
              <select v-model="formData.department" class="form-input" required :disabled="loading">
                <option value="">Select department</option>
                <option value="Finance">Finance</option>
                <option value="HR">Human Resources</option>
                <option value="Operations">Operations</option>
                <option value="IT">Information Technology</option>
                <option value="Sales">Sales</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Designation *</label>
              <input
                v-model="formData.designation"
                type="text"
                class="form-input"
                placeholder="Enter designation"
                required
                :disabled="loading"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Date of Joining *</label>
              <input
                v-model="formData.dateOfJoining"
                type="date"
                class="form-input"
                required
                :disabled="loading"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Confirmation Date</label>
              <input
                v-model="formData.confirmationDate"
                type="date"
                class="form-input"
                :disabled="loading"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Attendance Device RF Tag ID</label>
              <input
                v-model="formData.rfTagId"
                type="text"
                class="form-input"
                placeholder="e.g. RF0001234"
                :disabled="loading"
              />
            </div>
            <div class="form-group">
              <label class="form-label">CV / Resume</label>
              <div class="file-upload">
                <input
                  type="file"
                  class="file-input"
                  @change="handleFileUpload"
                  accept=".pdf,.doc,.docx"
                  :disabled="loading"
                />
                <span class="upload-icon">&#x1F4E4;</span>
                <span class="upload-text">{{ formData.cvFile ? formData.cvFile.name : 'Upload CV' }}</span>
              </div>
              <p class="upload-note">PDF, DOC or DOCX files accepted</p>
            </div>
          </div>
        </div>

        <!-- Salary & Bank Details Section -->
        <div class="form-section">
          <div class="section-header">
            <span class="section-icon">&#x1F4B0;</span>
            <h2 class="section-title">Salary & Bank Details</h2>
          </div>

          <div class="form-group">
            <label class="form-label">Basic Salary (Monthly) *</label>
            <input
              v-model.number="formData.basicSalary"
              type="number"
              class="form-input"
              placeholder="Enter basic salary"
              required
              :disabled="loading"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Bank Name</label>
              <input
                v-model="formData.bankName"
                type="text"
                class="form-input"
                placeholder="Enter bank name"
                :disabled="loading"
              />
            </div>
            <div class="form-group">
              <label class="form-label">Account Number</label>
              <input
                v-model="formData.accountNumber"
                type="text"
                class="form-input"
                placeholder="Enter account number"
                :disabled="loading"
              />
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="form-actions">
          <button type="button" class="btn-cancel" @click="handleCancel" :disabled="loading">
            Cancel
          </button>
          <button type="submit" class="btn-submit" :disabled="loading">
            <span v-if="loading" class="btn-spinner"></span>
            {{ submitButtonText }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'AddEmployee',
  data() {
    return {
      loadingEmployee: false,
      formData: {
        fullName: '',
        cnicNumber: '',
        gender: '',
        dateOfBirth: '',
        maritalStatus: '',
        personalEmail: '',
        phone: '',
        currentAddress: '',
        employmentType: '',
        businessEmail: '',
        department: '',
        designation: '',
        dateOfJoining: '',
        confirmationDate: '',
        rfTagId: '',
        cvFile: null,
        basicSalary: '',
        bankName: '',
        accountNumber: ''
      }
    }
  },
  computed: {
    ...mapGetters('employee', ['loading', 'currentEmployee']),
    isEditMode() {
      return !!this.$route.params.partyId
    },
    partyId() {
      return this.$route.params.partyId
    },
    submitButtonText() {
      if (this.loading) {
        return this.isEditMode ? 'Updating...' : 'Adding...'
      }
      return this.isEditMode ? 'Update Employee' : 'Add Employee'
    }
  },
  methods: {
    ...mapActions('employee', [
      'createEmployee',
      'updateEmployee',
      'fetchEmployeeById',
      'clearCurrentEmployee'
    ]),

    goBack() {
      this.$router.back()
    },

    handleFileUpload(event) {
      this.formData.cvFile = event.target.files[0]
    },

    async loadEmployee() {
      if (!this.partyId) return

      this.loadingEmployee = true
      try {
        const employee = await this.fetchEmployeeById(this.partyId)
        if (employee) {
          this.formData = {
            fullName: employee.name || '',
            cnicNumber: employee.chcNumber || '',
            gender: employee.gender || '',
            dateOfBirth: employee.dateOfBirth ? employee.dateOfBirth.split('T')[0] : '',
            maritalStatus: employee.maritalStatus || '',
            personalEmail: employee.email || '',
            phone: employee.phone || '',
            currentAddress: employee.currentAddress || '',
            employmentType: employee.employmentType || '',
            businessEmail: employee.businessEmail || '',
            department: employee.department || '',
            designation: employee.designation || '',
            dateOfJoining: employee.dateOfJoining ? employee.dateOfJoining.split('T')[0] : '',
            confirmationDate: employee.confirmationDate ? employee.confirmationDate.split('T')[0] : '',
            rfTagId: employee.rfTagId || '',
            cvFile: null,
            basicSalary: employee.basicSalary || '',
            bankName: employee.bankName || '',
            accountNumber: employee.accountNumber || ''
          }
        }
      } catch (error) {
        console.error('Failed to load employee:', error)
        this.$router.push('/salaries')
      } finally {
        this.loadingEmployee = false
      }
    },

    async handleSubmit() {
      const payload = {
        name: this.formData.fullName,
        chcNumber: this.formData.cnicNumber || null,
        gender: this.formData.gender || null,
        dateOfBirth: this.formData.dateOfBirth || null,
        maritalStatus: this.formData.maritalStatus || null,
        email: this.formData.personalEmail || null,
        phone: this.formData.phone || null,
        currentAddress: this.formData.currentAddress || null,
        employmentType: this.formData.employmentType,
        businessEmail: this.formData.businessEmail || null,
        department: this.formData.department,
        designation: this.formData.designation,
        dateOfJoining: this.formData.dateOfJoining,
        confirmationDate: this.formData.confirmationDate || null,
        rfTagId: this.formData.rfTagId || null,
        basicSalary: parseFloat(this.formData.basicSalary) || 0,
        bankName: this.formData.bankName || null,
        accountNumber: this.formData.accountNumber || null
      }

      try {
        if (this.isEditMode) {
          await this.updateEmployee({
            partyId: this.partyId,
            payload
          })
        } else {
          await this.createEmployee(payload)
        }
        this.$router.push('/salaries')
      } catch (error) {
        console.error('Submit failed:', error)
      }
    },

    handleCancel() {
      this.$router.back()
    }
  },
  created() {
    if (this.isEditMode) {
      this.loadEmployee()
    }
  },
  beforeDestroy() {
    this.clearCurrentEmployee()
  }
}
</script>

<style scoped>
.header {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 40px;
}

.back-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  color: #6b7280;
  transition: color 0.2s;
}

.back-button:hover {
  color: #1f2937;
}

.back-arrow {
  font-size: 24px;
}

.header-content {
  flex: 1;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #9ca3af;
  margin: 0;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  margin-top: 16px;
  color: #6b7280;
}

/* Form Sections */
.employee-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.form-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.section-icon {
  font-size: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

/* Form Rows and Groups */
.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.form-label::after {
  content: attr(data-required);
}

/* Inputs */
.form-input,
.form-textarea {
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s;
  background: #ffffff;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-input:disabled,
.form-textarea:disabled {
  background: #f9fafb;
  cursor: not-allowed;
}

.form-input::placeholder {
  color: #d1d5db;
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

/* File Upload */
.file-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
}

.file-upload:hover {
  border-color: #2563eb;
  background: #eff6ff;
}

.file-input {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.file-input:disabled {
  cursor: not-allowed;
}

.upload-icon {
  font-size: 24px;
  margin-right: 8px;
}

.upload-text {
  font-size: 14px;
  font-weight: 500;
  color: #2563eb;
}

.upload-note {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 8px;
  margin-bottom: 0;
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 32px;
}

.btn-submit,
.btn-cancel {
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-submit {
  background: #2563eb;
  color: white;
}

.btn-submit:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-cancel {
  background: #e5e7eb;
  color: #374151;
}

.btn-cancel:hover:not(:disabled) {
  background: #d1d5db;
}

.btn-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Responsive */
@media (max-width: 768px) {
  .page-title {
    font-size: 24px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-submit,
  .btn-cancel {
    width: 100%;
    justify-content: center;
  }
}
</style>
